/* eslint-disable no-console */
import { storageClient, StorageKey, ThemeName } from "@kaggle/core";
import { LocalState } from "../kernels/redux/types/LocalState";
import { EditorSettings } from "../kernels/types/EditorSettings";
import { LanguageId } from "../kernels/types/language";
import {
  KernelStorageClient,
  LocalStateCallback,
} from "../kernels/types/services/KernelStorageClient";

const createKernelStorageClient = (): KernelStorageClient => {
  const clearEditorSettings = () => {
    storageClient.delete(StorageKey.KAGGLE_EDITOR);
  };

  const clearLocalState = () => {
    storageClient.delete(StorageKey.KAGGLE_LOCAL_STATE);
  };

  const readCenteredLayoutMode = (): boolean => {
    return (
      storageClient.read(StorageKey.KAGGLE_EDITOR_CENTERED_LAYOUT_MODE) ===
      "true"
    );
  };

  const readCorgiMode = (): boolean => {
    return storageClient.read(StorageKey.KAGGLE_CORGI_MODE) === "true";
  };

  const readEditorSettings = (): Partial<EditorSettings> => {
    const raw = storageClient.read(StorageKey.KAGGLE_EDITOR);
    if (raw === null) {
      return {};
    }
    try {
      const editorSettings = JSON.parse(raw) as Partial<EditorSettings>;
      if (Array.isArray(editorSettings)) {
        throw new Error("editorSettings should not be persisted as an array.");
      }
      if (!Array.isArray(editorSettings.enabledViews)) {
        throw new Error("editorSettings should have an enabledViews array.");
      }
      if (!Array.isArray(editorSettings.expandedViews)) {
        throw new Error("editorSettings should have an expandedViews array.");
      }
      return editorSettings;
    } catch (error) {
      console.warn(`Error reading device settings: ${error.message}`);
      clearEditorSettings();
    }
    return {};
  };

  const readKittyMode = (): boolean => {
    return storageClient.read(StorageKey.KAGGLE_KITTY_MODE) === "true";
  };

  const readLanguageId = (): LanguageId | null => {
    const strLanguage = storageClient.read(StorageKey.KAGGLE_LANGUAGE_ID);
    if (!strLanguage) {
      return null;
    }
    return parseInt(strLanguage, 10) as LanguageId;
  };

  const readLocalState = (): Partial<LocalState> => {
    const raw = storageClient.read(StorageKey.KAGGLE_LOCAL_STATE);
    if (raw === null) {
      return {};
    }
    try {
      const state = JSON.parse(raw) as Partial<LocalState>;
      if (state && state.sessions) {
        if (!Array.isArray(state.sessions)) {
          throw new Error("localState.kernelRuns should be an array.");
        }
      }
      return state;
    } catch (error) {
      console.warn(`Error reading local state: ${error.message}`);
      clearLocalState();
    }
    return {};
  };

  const readThemeName = (): ThemeName | null => {
    const raw = storageClient.read(StorageKey.KAGGLE_THEME);
    if (raw === null) {
      return null;
    }
    return raw as ThemeName;
  };

  const writeCenteredLayoutMode = (enabled: boolean) => {
    storageClient.write(
      StorageKey.KAGGLE_EDITOR_CENTERED_LAYOUT_MODE,
      enabled.toString()
    );
  };

  const writeCorgiMode = (enabled: boolean) => {
    storageClient.write(StorageKey.KAGGLE_CORGI_MODE, enabled.toString());
  };

  const writeEditorSettings = (editorSettings: EditorSettings) => {
    storageClient.write(
      StorageKey.KAGGLE_EDITOR,
      JSON.stringify(editorSettings)
    );
  };

  const writeKittyMode = (enabled: boolean) => {
    storageClient.write(StorageKey.KAGGLE_KITTY_MODE, enabled.toString());
  };

  const writeLanguageId = (state: LanguageId) => {
    storageClient.write(StorageKey.KAGGLE_LANGUAGE_ID, state.toString());
  };

  const writeLocalState = (state: LocalState) => {
    storageClient.write(StorageKey.KAGGLE_LOCAL_STATE, JSON.stringify(state));
  };

  const writeThemeName = (themeName: ThemeName) => {
    storageClient.write(StorageKey.KAGGLE_THEME, themeName);
  };

  const subscribeLocalState = (callback: LocalStateCallback) => {
    return storageClient.subscribe(
      StorageKey.KAGGLE_LOCAL_STATE,
      (_key, value) => {
        if (value === null) {
          return;
        }

        const state = JSON.parse(value) as LocalState;
        const isValid =
          state && state.sessions && Array.isArray(state.sessions);
        if (!isValid) {
          return;
        }
        callback(state);
      }
    );
  };

  return {
    clearEditorSettings,
    clearLocalState,
    readCenteredLayoutMode,
    readCorgiMode,
    readEditorSettings,
    readKittyMode,
    readLanguageId,
    readLocalState,
    readThemeName,
    writeCenteredLayoutMode,
    writeCorgiMode,
    writeEditorSettings,
    writeKittyMode,
    writeLanguageId,
    writeLocalState,
    writeThemeName,
    subscribeLocalState,
  };
};

const createMockKernelStorageClient = (): KernelStorageClient => {
  return {
    clearEditorSettings: jest.fn(),
    clearLocalState: jest.fn(),
    readCenteredLayoutMode: jest.fn(),
    readCorgiMode: jest.fn(),
    readEditorSettings: jest.fn(),
    readKittyMode: jest.fn(),
    readLanguageId: jest.fn(),
    readLocalState: jest.fn(),
    readThemeName: jest.fn(),
    writeCenteredLayoutMode: jest.fn(),
    writeCorgiMode: jest.fn(),
    writeEditorSettings: jest.fn(),
    writeKittyMode: jest.fn(),
    writeLanguageId: jest.fn(),
    writeLocalState: jest.fn(),
    writeThemeName: jest.fn(),
    subscribeLocalState: jest.fn(),
  };
};

export const kernelStorageClient = (window as any).__MOCK_SERVICES__
  ? createMockKernelStorageClient()
  : createKernelStorageClient();
